import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export type PeriodStatus = "not_started" | "in_progress" | "signed_off";

export type CapacityMethod = "actual" | "target_rate" | "marginal" | "none";

export interface ProductLine {
  key: string;
  order: number;
  title: string;
  metric_label: string;
  unit_label: string;
  capacity_method: CapacityMethod;
  target_rate_excl_tax: number | null;
  notes: string;
  custom: boolean;
  active: boolean;
}

export interface Vendor {
  id: string;
  key: string;
  name: string;
  active: boolean;
}

export interface PeriodSummary {
  period: string; // YYYY-MM
  status: PeriodStatus;
  exists: boolean;
  signed_off_at: string | null;
  signed_off_by: string | null;
  signed_off_by_name: string | null;
}

export interface PeriodDetail extends PeriodSummary {
  notes: string | null;
  use_workbook_totals: boolean;
  can_open: boolean;
  can_write: boolean;
  can_sign_off: boolean;
}

const KEY = "cos_units";

export function useProductLines() {
  return useQuery({
    queryKey: [KEY, "product-lines"],
    queryFn: () => apiFetch<ProductLine[]>("/cos-units/product-lines"),
    staleTime: 5 * 60_000,
  });
}

export function useVendors(enabled = true) {
  return useQuery({
    queryKey: [KEY, "vendors"],
    queryFn: () => apiFetch<Vendor[]>("/cos-units/vendors"),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCosPeriods() {
  return useQuery({
    queryKey: [KEY, "periods"],
    queryFn: () => apiFetch<PeriodSummary[]>("/cos-units/periods"),
    staleTime: 60_000,
  });
}

export function useCosPeriod(period: string) {
  return useQuery({
    queryKey: [KEY, "period", period],
    queryFn: () => apiFetch<PeriodDetail>(`/cos-units/periods/${period}`),
    enabled: !!period,
    staleTime: 60_000,
  });
}

export function useOpenCosPeriod(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<PeriodDetail>(`/cos-units/periods/${period}/open`, { method: "POST" }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "period", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
    },
  });
}

export function useSignOffCosPeriod(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notes?: string) =>
      apiFetch<PeriodDetail>(`/cos-units/periods/${period}/sign-off`, {
        method: "POST",
        body: { notes: notes ?? null },
      }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "period", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
      // Sign-off freezes the snapshot the outputs/trends reads prefer.
      void qc.invalidateQueries({ queryKey: [KEY, "outputs", period] });
      void qc.invalidateQueries({ queryKey: [KEY, "trends"] });
    },
  });
}

export function useReopenCosPeriod(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<PeriodDetail>(`/cos-units/periods/${period}/reopen`, { method: "POST" }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "period", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
      void qc.invalidateQueries({ queryKey: [KEY, "outputs", period] });
      void qc.invalidateQueries({ queryKey: [KEY, "trends"] });
    },
  });
}

// ── uploads ─────────────────────────────────────────────────────────────────

export type UploadStatus = "pending" | "parsed" | "failed";

export interface UploadRead {
  id: string;
  period: string;
  filename: string;
  kind: string;
  status: UploadStatus;
  row_count: number | null;
  message: string | null;
  parser_version: string | null;
  uploaded_by_name: string | null;
  created_at: string;
}

export function useCosUploads(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "uploads", period],
    queryFn: () => apiFetch<UploadRead[]>(`/cos-units/periods/${period}/uploads`),
    enabled: enabled && !!period,
    retry: false,
  });
}

/** Everything that changes when the period's rows change. */
function invalidatePeriod(qc: ReturnType<typeof useQueryClient>, period: string) {
  void qc.invalidateQueries({ queryKey: [KEY, "uploads", period] });
  void qc.invalidateQueries({ queryKey: [KEY, "outputs", period] });
  void qc.invalidateQueries({ queryKey: [KEY, "line-items", period] });
  void qc.invalidateQueries({ queryKey: [KEY, "metrics", period] });
  void qc.invalidateQueries({ queryKey: [KEY, "trends"] });
  void qc.invalidateQueries({ queryKey: [KEY, "period", period] });
  void qc.invalidateQueries({ queryKey: [KEY, "vendor-coverage", period] });
  void qc.invalidateQueries({ queryKey: [KEY, "flow-map", period] });
  void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
}

export function useUploadCosFiles(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ files, model }: { files: File[]; model?: string | null }) => {
      const form = new FormData();
      for (const f of files) form.append("files", f);
      if (model) form.append("model", model);
      return apiFetch<UploadRead[]>(`/cos-units/periods/${period}/uploads`, {
        method: "POST",
        body: form,
      });
    },
    onSuccess: () => invalidatePeriod(qc, period),
  });
}

export function useReparseCosUpload(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uploadId: string) =>
      apiFetch<UploadRead>(`/cos-units/periods/${period}/uploads/${uploadId}/reparse`, {
        method: "POST",
      }),
    onSuccess: () => invalidatePeriod(qc, period),
  });
}

export function useDeleteCosUpload(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uploadId: string) =>
      apiFetch<void>(`/cos-units/periods/${period}/uploads/${uploadId}`, { method: "DELETE" }),
    onSuccess: () => invalidatePeriod(qc, period),
  });
}

// ── line items ──────────────────────────────────────────────────────────────

export type LineItemStatus = "unmapped" | "mapped" | "excluded";

export interface LineItemRead {
  id: string;
  source_sheet: string | null;
  description: string | null;
  raw_amount: number | null;
  qty: number | null;
  status: LineItemStatus;
  monthly_cogs_amount: number | null;
  product_line_key: string | null;
  charge_type_id: string | null;
  charge_type_name: string | null;
  vendor_name: string | null;
  mapping_id: string | null;
  suggested_line_key: string | null;
  upload_filename: string | null;
  manual_product_line_key: string | null;
  manual_excluded: boolean;
  vendor_raw: string | null;
}

export interface LineItemPage {
  items: LineItemRead[];
  count: number;
  limit: number;
  offset: number;
  total_rows: number;
  unmapped_rows: number;
  excluded_rows: number;
}

export interface LineItemFilters {
  status?: LineItemStatus | "";
  sheet?: string;
  line?: string;
  q?: string;
  limit: number;
  offset: number;
}

export function useCosLineItems(period: string, filters: LineItemFilters, enabled = true) {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.sheet) params.set("sheet", filters.sheet);
  if (filters.line) params.set("line", filters.line);
  if (filters.q) params.set("q", filters.q);
  params.set("limit", String(filters.limit));
  params.set("offset", String(filters.offset));
  return useQuery({
    queryKey: [KEY, "line-items", period, Object.fromEntries(params)],
    queryFn: () =>
      apiFetch<LineItemPage>(`/cos-units/periods/${period}/line-items?${params.toString()}`),
    enabled: enabled && !!period,
    retry: false,
    placeholderData: (prev) => prev,
  });
}

// ── outputs + trends ────────────────────────────────────────────────────────

export interface LineOutputRead {
  product_line_key: string;
  title: string;
  metric_label: string;
  total_expense: number | null;
  metric_units: number | null;
  metric_source: string | null;
  dollar_per_metric_actual: number | null;
  full_cap_per_metric_excl: number | null;
  full_cap_per_metric_incl: number | null;
  utilization: number | null;
  workbook_total: number | null;
  is_snapshot: boolean;
  computed_at: string | null;
}

export interface PeriodOutputs {
  period: string;
  status: PeriodStatus;
  lines: LineOutputRead[];
  grand_total: number;
  unmapped_rows: number;
  computed_at: string | null;
}

export function useCosOutputs(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "outputs", period],
    queryFn: () => apiFetch<PeriodOutputs>(`/cos-units/periods/${period}/outputs`),
    enabled: enabled && !!period,
    retry: false,
  });
}

export function useRecompute(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<PeriodOutputs>(`/cos-units/periods/${period}/recompute`, { method: "POST" }),
    onSuccess: () => invalidatePeriod(qc, period),
  });
}

export interface TrendPoint {
  period: string;
  status: PeriodStatus;
  total_expense: number | null;
  metric_units: number | null;
  dollar_per_metric_actual: number | null;
  full_cap_per_metric_excl: number | null;
  utilization: number | null;
  is_snapshot: boolean;
}

export interface TrendLine {
  product_line_key: string;
  title: string;
  metric_label: string;
  unit_label: string;
  points: TrendPoint[];
}

export interface TrendsRead {
  months: string[];
  lines: TrendLine[];
  grand_totals: (number | null)[];
}

export function useCosTrends(months = 12) {
  return useQuery({
    queryKey: [KEY, "trends", months],
    queryFn: () => apiFetch<TrendsRead>(`/cos-units/trends?months=${months}`),
    staleTime: 60_000,
  });
}

// ── metrics (denominators) ──────────────────────────────────────────────────

export interface PeriodMetricRead {
  product_line_key: string;
  metric_units: number | null;
  source: string | null;
  note: string | null;
}

export function useCosMetrics(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "metrics", period],
    queryFn: () => apiFetch<PeriodMetricRead[]>(`/cos-units/periods/${period}/metrics`),
    enabled: enabled && !!period,
    retry: false,
  });
}

export function useSetCosMetric(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { product_line_key: string; metric_units: number | null; note?: string }) =>
      apiFetch<PeriodMetricRead[]>(`/cos-units/periods/${period}/metrics`, {
        method: "PUT",
        body: payload,
      }),
    onSuccess: () => invalidatePeriod(qc, period),
  });
}

// ── mappings ────────────────────────────────────────────────────────────────

export interface MappingSplitRead {
  target_product_line_key: string | null;
  fixed_pct: number | null;
  computed_method_key: string | null;
}

export interface ChargeTypeRead {
  id: string;
  vendor_key: string;
  vendor_name: string;
  key: string;
  canonical_name: string;
  match_kind: string;
  match_pattern: string | null;
  source_sheet: string | null;
  active: boolean;
  mapping_id: string | null;
  product_line_key: string | null;
  amortization: string | null;
  sign: string | null;
  include_in_cogs: boolean | null;
  allocation_method: string | null;
  splits: MappingSplitRead[];
  mapping_notes: string | null;
}

export function useChargeTypes(enabled = true) {
  return useQuery({
    queryKey: [KEY, "charge-types"],
    queryFn: () => apiFetch<ChargeTypeRead[]>("/cos-units/charge-types"),
    enabled,
    staleTime: 60_000,
  });
}

export interface AssignRequest {
  vendor_key: string;
  canonical_name: string;
  product_line_key: string | null;
  match_kind: "sheet" | "exact" | "regex";
  match_pattern: string | null;
  amortization: "none" | "monthly" | "annual_12" | "quarterly_3";
  sign: "charge" | "credit";
  include_in_cogs: boolean;
  notes?: string | null;
}

export function useAssignLineItem(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: AssignRequest }) =>
      apiFetch<void>(`/cos-units/periods/${period}/line-items/${itemId}/assign`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: () => {
      invalidatePeriod(qc, period);
      void qc.invalidateQueries({ queryKey: [KEY, "charge-types"] });
      void qc.invalidateQueries({ queryKey: [KEY, "changelog"] });
    },
  });
}

export interface RowOverrideRequest {
  product_line_key: string | null;
  excluded: boolean;
  reason?: string | null;
}

/** One-off override for a single row — no rule is created; empty payload clears. */
export function useOverrideLineItem(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: RowOverrideRequest }) =>
      apiFetch<void>(`/cos-units/periods/${period}/line-items/${itemId}/override`, {
        method: "PATCH",
        body: payload,
      }),
    onSuccess: () => {
      invalidatePeriod(qc, period);
      void qc.invalidateQueries({ queryKey: [KEY, "changelog"] });
    },
  });
}

export interface MappingUpdate {
  product_line_key?: string | null;
  amortization?: "none" | "monthly" | "annual_12" | "quarterly_3";
  sign?: "charge" | "credit";
  include_in_cogs?: boolean;
  notes?: string | null;
  reason?: string | null;
}

export function useUpdateMapping(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mappingId, payload }: { mappingId: string; payload: MappingUpdate }) =>
      apiFetch<void>(`/cos-units/mappings/${mappingId}?period=${period}`, {
        method: "PATCH",
        body: payload,
      }),
    onSuccess: () => {
      invalidatePeriod(qc, period);
      void qc.invalidateQueries({ queryKey: [KEY, "charge-types"] });
      void qc.invalidateQueries({ queryKey: [KEY, "changelog"] });
    },
  });
}

export interface ChangelogRead {
  id: string;
  action: string;
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  reason: string | null;
  charge_type_name: string | null;
  changed_by_name: string | null;
  created_at: string;
}

export function useMappingChangelog(limit = 50, enabled = true) {
  return useQuery({
    queryKey: [KEY, "changelog", limit],
    queryFn: () => apiFetch<ChangelogRead[]>(`/cos-units/mappings/changelog?limit=${limit}`),
    enabled,
  });
}

export function useSeedDefaults(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ vendors_created: number; rules_created: number }>(
        "/cos-units/mappings/seed-defaults",
        { method: "POST" },
      ),
    onSuccess: () => {
      invalidatePeriod(qc, period);
      void qc.invalidateQueries({ queryKey: [KEY, "charge-types"] });
      void qc.invalidateQueries({ queryKey: [KEY, "changelog"] });
    },
  });
}

// ── calc settings ───────────────────────────────────────────────────────────

export type MetricSource = "invoice" | "recon" | "cinch";

export interface CalcSettingRead {
  product_line_key: string;
  title: string;
  metric_label: string;
  unit_label: string;
  capacity_method: CapacityMethod;
  target_rate_excl_tax: number | null;
  target_rate_incl_tax: number | null;
  metric_source: MetricSource | null;
  notes: string | null;
  is_override: boolean;
  is_custom: boolean;
}

export interface CalcSettingWrite {
  capacity_method: CapacityMethod;
  target_rate_excl_tax: number | null;
  target_rate_incl_tax: number | null;
  metric_source?: MetricSource | null;
  notes: string | null;
}

export interface ProductLineCreate {
  title: string;
  key?: string | null;
  metric_label?: string;
  unit_label?: string;
  capacity_method?: CapacityMethod;
  target_rate_excl_tax?: number | null;
  metric_source?: MetricSource | null;
  notes?: string | null;
}

export function useCreateProductLine(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductLineCreate) =>
      apiFetch<ProductLine>("/cos-units/product-lines", { method: "POST", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "product-lines"] });
      void qc.invalidateQueries({ queryKey: [KEY, "calc-settings"] });
      void qc.invalidateQueries({ queryKey: [KEY, "changelog"] });
      invalidatePeriod(qc, period);
    },
  });
}

export function useUpdateProductLine(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, payload }: { key: string; payload: { active?: boolean; title?: string } }) =>
      apiFetch<void>(`/cos-units/product-lines/${key}`, { method: "PATCH", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "product-lines"] });
      void qc.invalidateQueries({ queryKey: [KEY, "calc-settings"] });
      invalidatePeriod(qc, period);
    },
  });
}

export function useCalcSettings(enabled = true) {
  return useQuery({
    queryKey: [KEY, "calc-settings"],
    queryFn: () => apiFetch<CalcSettingRead[]>("/cos-units/calc-settings"),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCalcSetting(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lineKey, payload }: { lineKey: string; payload: CalcSettingWrite }) =>
      apiFetch<CalcSettingRead[]>(`/cos-units/calc-settings/${lineKey}?period=${period}`, {
        method: "PUT",
        body: payload,
      }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "calc-settings"], data);
      invalidatePeriod(qc, period);
    },
  });
}

export function useResetCalcSetting(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineKey: string) =>
      apiFetch<CalcSettingRead[]>(`/cos-units/calc-settings/${lineKey}?period=${period}`, {
        method: "DELETE",
      }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "calc-settings"], data);
      invalidatePeriod(qc, period);
    },
  });
}

// ── formatting helpers ──────────────────────────────────────────────────────

/** Money to 2 decimals (these figures reconcile to invoices — no $K compaction). */
export function fmtMoney(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `−$${abs}` : `$${abs}`;
}

/** Whole dollars, for tiles where cents are noise. */
export function fmtMoney0(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `$${Math.round(Number(n)).toLocaleString("en-US")}`;
}

/** $/metric rates run sub-cent ($0.00029/email) — show enough precision. */
export function fmtRate(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const digits = Math.abs(v) >= 1 ? 2 : Math.abs(v) >= 0.01 ? 4 : 6;
  return `$${v.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

export function fmtQty(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Math.round(Number(n)).toLocaleString("en-US");
}

export function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${(Number(n) * 100).toFixed(1)}%`;
}

// ── month helpers ──────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Current calendar month as "YYYY-MM". */
export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** "2026-05" → "May 2026". */
export function monthLabel(ym: string | null | undefined): string {
  if (!ym || ym.length < 7) return "—";
  const name = MONTH_NAMES[Number(ym.slice(5, 7)) - 1];
  return name ? `${name} ${ym.slice(0, 4)}` : ym;
}

/** The current month and the prior `count - 1` months, newest first. */
export function recentMonths(count = 18): string[] {
  const out: string[] = [];
  const d = new Date();
  let y = d.getFullYear();
  let m = d.getMonth(); // 0-based
  for (let i = 0; i < count; i++) {
    out.push(`${y}-${String(m + 1).padStart(2, "0")}`);
    m -= 1;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
  }
  return out;
}

// ── vendors tab ─────────────────────────────────────────────────────────────

export interface VendorCoverageRow {
  id: string;
  key: string;
  name: string;
  active: boolean;
  rule_count: number;
  charge_type_count: number;
  item_count: number;
  amount: number | null;
  last_seen_period: string | null;
  product_line_keys: string[];
}

export interface VendorCoverage {
  period: string;
  vendors: VendorCoverageRow[];
  missing_this_period: number;
}

export function useVendorCoverage(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "vendor-coverage", period],
    queryFn: () => apiFetch<VendorCoverage>(`/cos-units/periods/${period}/vendor-coverage`),
    enabled: enabled && !!period,
  });
}

export function useCreateVendor(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; key?: string | null }) =>
      apiFetch<VendorCoverageRow>("/cos-units/vendors", { method: "POST", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "vendor-coverage"] });
      void qc.invalidateQueries({ queryKey: [KEY, "vendors"] });
      void qc.invalidateQueries({ queryKey: [KEY, "changelog"] });
      void qc.invalidateQueries({ queryKey: [KEY, "flow-map", period] });
    },
  });
}

export function useUpdateVendor(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vendorId, payload }: {
      vendorId: string;
      payload: { name?: string | null; active?: boolean | null };
    }) =>
      apiFetch<void>(`/cos-units/vendors/${vendorId}`, { method: "PATCH", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "vendor-coverage"] });
      void qc.invalidateQueries({ queryKey: [KEY, "vendors"] });
      void qc.invalidateQueries({ queryKey: [KEY, "changelog"] });
      void qc.invalidateQueries({ queryKey: [KEY, "flow-map", period] });
    },
  });
}

// ── visual map ──────────────────────────────────────────────────────────────

export interface FlowNode {
  id: string;
  column: "vendor" | "charge" | "line";
  label: string;
  amount: number | null;
  tone: "excluded" | "unmapped" | null;
}

export interface FlowLink {
  source: string;
  target: string;
  amount: number | null;
  split: boolean;
  excluded: boolean;
}

export interface FlowMapRead {
  period: string;
  nodes: FlowNode[];
  links: FlowLink[];
}

export function useFlowMap(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "flow-map", period],
    queryFn: () => apiFetch<FlowMapRead>(`/cos-units/periods/${period}/flow-map`),
    enabled: enabled && !!period,
  });
}

// ── record mode (legacy months) ─────────────────────────────────────────────

export function useSetRecordMode(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (useWorkbookTotals: boolean) =>
      apiFetch<void>(`/cos-units/periods/${period}/record-mode`, {
        method: "PATCH",
        body: { use_workbook_totals: useWorkbookTotals },
      }),
    onSuccess: () => invalidatePeriod(qc, period),
  });
}


// ── AI extraction + review ──────────────────────────────────────────────────

export interface AiConfig {
  models: string[];
  default_model: string;
  key_configured: boolean;
}

export function useAiConfig() {
  return useQuery({
    queryKey: [KEY, "ai-config"],
    queryFn: () => apiFetch<AiConfig>("/cos-units/ai-config"),
    staleTime: 5 * 60_000,
  });
}

export interface AiRecommendation {
  severity: "action" | "warn" | "info" | string;
  title: string;
  detail: string;
}

export interface AiReviewRead {
  id: string;
  period: string;
  model: string;
  summary: string | null;
  recommendations: AiRecommendation[];
  created_at: string;
  created_by_name: string | null;
}

export function useAiReview(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "ai-review", period],
    queryFn: () => apiFetch<AiReviewRead | null>(`/cos-units/periods/${period}/ai-review`),
    enabled: enabled && !!period,
  });
}

export function useRunAiReview(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (model?: string | null) =>
      apiFetch<AiReviewRead>(`/cos-units/periods/${period}/ai-review`, {
        method: "POST",
        body: { model: model ?? null },
      }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "ai-review", period], data);
    },
  });
}

// ── R-18 recon sync ─────────────────────────────────────────────────────────

export interface ReconSyncStatus {
  available: boolean;
  invoice_count: number;
}

export interface ReconSyncResult {
  created: number;
  skipped: { invoice_number: string; vendor: string; reason: string }[];
}

export function useReconSyncStatus(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "recon-sync", period],
    queryFn: () => apiFetch<ReconSyncStatus>(`/cos-units/periods/${period}/recon-sync`),
    enabled: enabled && !!period,
  });
}

export function useSyncRecon(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<ReconSyncResult>(`/cos-units/periods/${period}/recon-sync`, { method: "POST" }),
    onSuccess: () => {
      invalidatePeriod(qc, period);
      void qc.invalidateQueries({ queryKey: [KEY, "recon-sync", period] });
    },
  });
}
